import { Link, useLocation } from 'react-router-dom';
import { useEffect, useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { BookOpen, MessageSquare, Calendar, Lightbulb, Video, Settings, LogOut, Shield, User, Mail, Menu, X, Users, Sparkles, Camera, Store, LayoutDashboard, CalendarCheck, Lock, Crown, HelpCircle } from 'lucide-react';
import NotificationBell from '@/components/NotificationBell';
import LegalReAckModal from '@/components/LegalReAckModal';
import { PlatinumEntryCard } from '@/components/platinum/PlatinumEntryCard';
import TierBadge from '@/components/TierBadge';
import ProfilePicture from '@/components/ProfilePicture';
import { UserProfilePopover } from '@/components/UserProfilePopover';
import ProfilePictureUpload from '@/components/ProfilePictureUpload';
import { useAudio } from '@/contexts/AudioContext';
import { useLanguage } from '@/contexts/LanguageContext';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import PractitionerOnboardingBanner from '@/components/PractitionerOnboardingBanner';
import { ViewAsBanner } from '@/components/admin/ViewAsBanner';
import CompanionTab from '@/components/companion/CompanionTab';

const NAV_KEYS = {
  'Coaching Program': 'nav.coaching_program',
  'Workshops': 'nav.workshops',
  'Forum Chat': 'nav.community',
  'Calendar': 'nav.calendar',
  'Resources/Guides': 'nav.resources',
  'Bookings': 'nav.bookings',
  'Live Stream': 'nav.live_stream',
  'Messages': 'nav.messages',
  'Marketplace': 'nav.marketplace',
  'Settings': 'nav.settings',
  'Practitioner Hub': 'nav.practitioner_hub',
  'Marketplace Hub': 'nav.marketplace_hub',
  'Admin': 'nav.admin',
};

// MenuContent component moved outside to prevent recreation on every render
const MenuContent = ({ user, menuItems, location, onLogout, membershipInfo, onProfilePictureClick, profilePicture, hasPlatinumAccess }) => {
  const { t } = useLanguage();
  return (
  <>
    <div className="mb-8">
      <div className="w-20 h-20 rounded-full overflow-hidden mx-auto">
        <img 
          src="https://customer-assets.emergentagent.com/job_eleven-collective/artifacts/56npyh3g_11-11%20Coaching%20App.jpg" 
          alt="11/11 Logo" 
          className="w-full h-full object-contain spiritual-glow"
        />
      </div>
      <h2 className="text-center mt-4 text-lg font-semibold" style={{display: 'none'}}>11/11 Collective Connection</h2>
      
      {/* Profile Picture - Clickable */}
      <div className="flex flex-col items-center mt-3">
        <div className="relative group">
          <ProfilePicture
            src={profilePicture}
            alt={user.full_name}
            size="lg"
            onClick={onProfilePictureClick}
          />
          <div 
            className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
            onClick={onProfilePictureClick}
          >
            <Camera className="w-5 h-5 text-white" />
          </div>
        </div>
        <p className="text-center text-xs text-gray-500 mt-1">Click to change</p>
      </div>
      
      <div className="flex items-center justify-center gap-2 mt-2">
        <UserProfilePopover userId={user.id} className="hover:opacity-80 transition-opacity">
          <p className="text-center text-sm text-gray-400 underline decoration-dotted underline-offset-2">{user.full_name}</p>
        </UserProfilePopover>
        <NotificationBell />
        <LanguageSwitcher />
      </div>
      {membershipInfo && (
        <div className="flex justify-center mt-2">
          <TierBadge 
            tier={membershipInfo.membership_tier} 
            platinumPlus={hasPlatinumAccess}
            size="sm"
          />
        </div>
      )}
    </div>
    
    <nav className="space-y-2">
      {menuItems.map((item) => {
        const Icon = item.icon;
        const isActive = location.pathname === item.path;
        
        return (
          <Link
            key={item.path}
            to={item.path}
            data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
            className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
              isActive 
                ? 'bg-cyan-500/20 text-cyan-400 border-l-4 border-cyan-400' 
                : 'text-gray-300 hover:bg-white/5 hover:text-white'
            }`}
          >
            <Icon className="w-5 h-5 flex-shrink-0" />
            <span className="font-medium">{t(NAV_KEYS[item.label], item.label)}</span>
          </Link>
        );
      })}
    </nav>
    
    <div className="mt-auto pt-6 border-t border-gray-700">
      <Button 
        data-testid="logout-btn"
        onClick={onLogout} 
        variant="ghost" 
        className="w-full flex items-center gap-3 text-gray-300 hover:text-white hover:bg-red-500/20"
      >
        <LogOut className="w-5 h-5 flex-shrink-0" />
        <span>{t('nav.logout', 'Logout')}</span>
      </Button>
    </div>
  </>
  );
};

const DashboardLayout = ({ children, user, onLogout }) => {
  const location = useLocation();
  const { fetchAndPlayMusic } = useAudio();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [membershipInfo, setMembershipInfo] = useState(null);
  const [showProfileUpload, setShowProfileUpload] = useState(false);
  const [profilePicture, setProfilePicture] = useState(user?.profile_picture || null);
  const [isApprovedVendor, setIsApprovedVendor] = useState(false);
  
  // Map routes to page names
  const getPageName = (pathname) => {
    if (pathname.includes('/practitioner-hub')) return 'practitioner-hub';
    if (pathname.includes('/live-stream')) return 'live-stream';
    if (pathname.includes('/modules')) return 'modules';
    if (pathname.includes('/workshops')) return 'workshops';
    if (pathname.includes('/special-access')) return 'special-access';
    if (pathname.includes('/forum')) return 'forum';
    if (pathname.includes('/calendar')) return 'calendar';
    if (pathname.includes('/resources')) return 'resources';
    if (pathname.includes('/settings')) return 'settings';
    if (pathname.includes('/messages')) return 'messages';
    if (pathname.includes('/marketplace')) return 'marketplace';
    if (pathname.includes('/bookings')) return 'bookings';
    return null;
  };

  // Fetch music when page changes
  useEffect(() => {
    const pageName = getPageName(location.pathname);
    if (pageName) {
      fetchAndPlayMusic(pageName);
    }
  }, [location.pathname, fetchAndPlayMusic]);

  // Close mobile menu when route changes - use callback to avoid setState-in-effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsMobileMenuOpen(false);
    }, 0);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  // Fetch membership info
  useEffect(() => {
    const fetchMembership = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/user/membership`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setMembershipInfo(response.data);
      } catch (error) {
        console.error('Failed to fetch membership:', error);
      }
    };
    fetchMembership();
  }, []);

  // Check for unread vendor approval notifications (toast on login)
  useEffect(() => {
    const checkApprovalNotifications = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/notifications`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const unreadApprovals = (res.data || []).filter(
          n => n.type === 'vendor_approved' && !n.is_read
        );
        if (unreadApprovals.length > 0) {
          toast.success(unreadApprovals[0].title, {
            description: unreadApprovals[0].message,
            duration: 8000,
          });
        }
      } catch (e) {}
    };
    checkApprovalNotifications();
  }, []);

  // Is this person an approved vendor? Re-checked whenever they move around the dashboard or
  // come back to the tab — it used to run once per mount, so a storefront approved while their
  // browser was open showed no Marketplace Hub until they reloaded (owner, Sep 2026).
  useEffect(() => {
    let cancelled = false;
    const checkVendorStatus = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/vendor/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!cancelled) setIsApprovedVendor(response.data?.status === 'approved');
      } catch (error) {
        // Not a vendor, or the application is still pending — that's fine.
        if (!cancelled) setIsApprovedVendor(false);
      }
    };
    checkVendorStatus();
    const onFocus = () => checkVendorStatus();
    window.addEventListener('focus', onFocus);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', onFocus);
    };
  }, [location.pathname]);

  // Platinum+ access — show the ribbon item only when eligible
  const [hasPlatinumAccess, setHasPlatinumAccess] = useState(false);
  const [announcePlatinum, setAnnouncePlatinum] = useState(false);
  useEffect(() => {
    // Nothing gets in front of the legal gate — hold the Platinum+ welcome until there is
    // nothing left for them to accept.
    Promise.all([
      axios.get(`${API}/platinum-plus/access`),
      axios.get(`${API}/legal/acknowledgements/pending`).catch(() => ({ data: { count: 0 } })),
    ])
      .then(([access, legal]) => {
        setHasPlatinumAccess(!!access.data.access);
        setAnnouncePlatinum(!!access.data.should_announce_entry && !(legal.data?.count > 0));
      })
      .catch(() => setHasPlatinumAccess(false));
  }, []);

  const dismissPlatinumEntry = () => {
    setAnnouncePlatinum(false);
    axios.post(`${API}/platinum-plus/entry-seen`).catch(() => {});
  };

  const menuItems = [
    { path: '/dashboard/modules', label: 'Coaching Program', icon: BookOpen },
    { path: '/dashboard/workshops', label: 'Workshops', icon: Users },
    { path: '/dashboard/forum', label: 'Forum Chat', icon: MessageSquare },
    { path: '/dashboard/calendar', label: 'Calendar', icon: Calendar },
    { path: '/dashboard/resources', label: 'Resources/Guides', icon: Lightbulb },
    { path: '/dashboard/bookings', label: 'Bookings', icon: CalendarCheck },
    { path: '/dashboard/live-stream', label: 'Live Stream', icon: Video },
    { path: '/dashboard/messages', label: 'Messages', icon: Mail },
    { path: '/dashboard/marketplace', label: 'Marketplace', icon: Store },
    { path: '/dashboard/questions', label: 'Questions', icon: HelpCircle },
    { path: '/dashboard/settings', label: 'Settings', icon: User },
  ];
  
  // Add Practitioner Hub for Free-Comp users (replaces Special Access + Vendor Dashboard)
  if (user.role === 'admin' || user.membership_type === 'free_comp') {
    menuItems.push({ path: '/dashboard/practitioner-hub', label: 'Practitioner Hub', icon: LayoutDashboard });
  }
  
  // Add Vendor → Marketplace Hub for approved non-Free-Comp vendors only.
  // Routes to PractitionerHub which renders as "Marketplace Hub" for non-eligible tiers.
  if (isApprovedVendor && user.membership_type !== 'free_comp' && user.role !== 'admin') {
    menuItems.push({ path: '/dashboard/practitioner-hub', label: 'Marketplace Hub', icon: LayoutDashboard });
  }
  
  // Add Admin panel for admin users
  if (user.role === 'admin') {
    menuItems.push({ path: '/dashboard/admin', label: 'Admin', icon: Shield });
  }

  // Platinum+ — appears only for eligible members (free_comp always; platinum after completing the gate section)
  if (hasPlatinumAccess) {
    menuItems.push({ path: '/dashboard/platinum-plus', label: 'Platinum+', icon: Crown });
  }

  return (
    // `overflow-hidden` here and on the content div below made them scroll containers,
    // which silently disabled `position: sticky` for every page inside the dashboard.
    <div className="flex min-h-screen relative overflow-clip">
      {announcePlatinum && <PlatinumEntryCard onDismiss={dismissPlatinumEntry} />}
      {/* Outer Space Background - Scorpio Constellation */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ 
          backgroundImage: 'url(https://images.unsplash.com/photo-1679615845580-8691c78fd7d3?q=80&w=2000)',
          zIndex: 0
        }}
      ></div>
      
      {/* Colored Overlay with Reduced Opacity */}
      <div 
        className="absolute inset-0" 
        style={{ 
          background: 'linear-gradient(to bottom, rgba(10, 14, 26, 0.7) 0%, rgba(26, 35, 50, 0.75) 50%, rgba(45, 55, 72, 0.8) 100%)',
          zIndex: 1
        }}
      ></div>
      
      <div className="flex min-h-screen w-full relative" style={{ zIndex: 10 }}>
        {/* Mobile Menu Button */}
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="lg:hidden fixed top-4 left-4 z-50 p-2 rounded-lg bg-slate-900/90 text-white hover:bg-slate-800 transition-colors"
        >
          {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>

        {/* Desktop Sidebar */}
        <div className="hidden lg:block w-64 bg-gradient-to-b from-slate-900 to-slate-800 text-white p-6 fixed h-full overflow-y-auto">
          <MenuContent 
            user={user}
            menuItems={menuItems}
            location={location}
            onLogout={onLogout}
            membershipInfo={membershipInfo}
            onProfilePictureClick={() => setShowProfileUpload(true)}
            profilePicture={profilePicture}
            hasPlatinumAccess={hasPlatinumAccess}
          />
        </div>

        {/* Mobile Sidebar Overlay */}
        {isMobileMenuOpen && (
          <div className="lg:hidden fixed inset-0 z-40">
            <div 
              className="absolute inset-0 bg-black/50" 
              onClick={() => setIsMobileMenuOpen(false)}
            />
            <div className="relative w-64 h-full bg-gradient-to-b from-slate-900 to-slate-800 text-white p-6 overflow-y-auto">
              <MenuContent 
                user={user}
                menuItems={menuItems}
                location={location}
                onLogout={onLogout}
                membershipInfo={membershipInfo}
                onProfilePictureClick={() => setShowProfileUpload(true)}
                profilePicture={profilePicture}
                hasPlatinumAccess={hasPlatinumAccess}
              />
            </div>
          </div>
        )}
      
      {/* Main Content */}
      <div className="lg:ml-64 flex-1 flex flex-col min-w-0">
        <ViewAsBanner />
        <div className="flex-1 p-4 pt-16 sm:p-6 lg:p-8 lg:pt-8 overflow-x-clip">
          {/* overflow-x-hidden here made this div a scroll container, which silently broke
              `position: sticky` for everything inside it (the Marketplace top bar). `clip`
              hides the same horizontal overflow without creating one. */}
          <PractitionerOnboardingBanner />
          {children}
        </div>

        <LegalReAckModal />
        
        {/* Footer with Legal Links */}
        <footer className="relative z-10 border-t border-gray-700 bg-black/40 backdrop-blur-sm p-4">
          <div className="flex justify-center items-center gap-6 text-sm">
            <a 
              href="/terms" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ color: '#2dd4bf' }}
              className="hover:underline"
            >
              Terms of Service
            </a>
            <span className="text-gray-500">|</span>
            <a 
              href="/privacy" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ color: '#2dd4bf' }}
              className="hover:underline"
            >
              Privacy Policy
            </a>
            <span className="text-gray-500">|</span>
            <a 
              href="/community-guidelines" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ color: '#2dd4bf' }}
              className="hover:underline"
            >
              Community Guidelines
            </a>
          </div>
        </footer>
      </div>
      </div>
      
      {/* Consciousness Companion — right-edge tab, follows the participant everywhere */}
      <CompanionTab user={user} />

      {/* Profile Picture Upload Modal */}
      <ProfilePictureUpload
        currentPicture={profilePicture}
        onUploadSuccess={(newPicture) => setProfilePicture(newPicture)}
        showModal={showProfileUpload}
        onClose={() => setShowProfileUpload(false)}
      />
    </div>
  );
};

export default DashboardLayout;